import { useI18n } from '../../i18n';
// ============================================
// StoresPage Component
// ============================================

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PageHeader, ActionButton, PageContainer, ExportButton, showToast, ImageUploadField } from '../../components/shared';
import { PlusIcon } from '../../components/icons';
import { useExport } from '../../hooks';
import {
  useStores,
  useStoresStats,
  useDeleteStore,
  useCreateStore,
  useUpdateStore,
  useStore,
} from '../../hooks/useStores';
import { StoreStatsRow } from './components/StoreStatsRow';
import { NodeDirectoryTable, type StoreTableRow, type FacilityStatus, type FacilityType } from './components/NodeDirectoryTable';
import type { AlcoholCurfewConfig, CreateStoreRequest, Store, StoreDetail, UpdateStoreRequest } from '@conai/shared-types';
import { StoreStatus } from '@conai/shared-types';
import { useIsAdmin } from '../../stores';
import { EUROPEAN_TIMEZONE_OPTIONS, formatTimezoneLabel, getCustomTimezoneOption } from '../../utils/timezones';
import { SUPPORTED_CURRENCIES, currencySymbol } from '../../utils/currency';

type ModalMode = 'view' | 'create' | 'edit' | null;

type DayHours = { open: string; close: string; closed: boolean };
type OperatingHours = Record<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday', DayHours>;

const DAYS: Array<keyof OperatingHours> = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<keyof OperatingHours, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

const defaultDayHours = (open = '08:00', close = '22:00'): DayHours => ({ open, close, closed: false });
const defaultOperatingHours = (): OperatingHours => ({
  monday: defaultDayHours(), tuesday: defaultDayHours(), wednesday: defaultDayHours(),
  thursday: defaultDayHours(), friday: defaultDayHours(),
  saturday: defaultDayHours('09:00', '21:00'), sunday: defaultDayHours('09:00', '21:00'),
});

/**
 * Trạng thái CHỈ-ĐỌC của cấu hình giờ cấm, chép nguyên từ
 * `alcohol_curfew_config` lúc nạp form.
 *
 * Không phải ô nhập: không nằm trong `formFieldOrder`, không đi vào payload,
 * không có validation. Nó tồn tại để form GIẢI THÍCH được vì sao một cấu hình
 * đang bật lại không có hiệu lực hôm nay — thứ mà trước đây người dùng chỉ thấy
 * dưới dạng "cấu hình biến mất" (BUG #46).
 *
 * Đây là ẢNH CHỤP lúc nạp từ server, KHÔNG tính lại theo thứ người dùng đang
 * gõ: tick "uống tại chỗ" xong thì mấy cờ này vẫn là của lần lưu trước, chỉ đúng
 * lại sau khi Lưu và refetch. Đúng như vậy là được — nó mô tả trạng thái ĐANG
 * LƯU trên server, không phải trạng thái dự đoán.
 */
type CurfewStatus = {
  /**
   * Bản BE đang chạy CÓ trả `alcohol_curfew_config` hay không.
   *
   * ⚠️ GUARD TẠM, TỰ TIÊU BIẾN — KHÔNG CẦN GỠ TAY.
   *
   * FE đã sửa theo hợp đồng BE mới (`alcohol_curfew_config` luôn là object),
   * nhưng bản BE mới CHƯA deploy lên QA. Trên BE cũ field này VẮNG MẶT, và khi
   * đó FE KHÔNG BIẾT trạng thái thật của giờ cấm trên server: checkbox sẽ hiện
   * TẮT chỉ vì thiếu dữ liệu, chứ không phải vì cửa hàng thật sự tắt.
   *
   * Nếu cứ gửi nhóm `alcohol_curfew` trong tình huống đó thì "vào sửa số điện
   * thoại rồi bấm Lưu" = TẮT THẬT quy định giờ cấm trên server, mất luôn
   * `legal_basis`/`effective_from` — BUG #46 ở dạng nặng hơn.
   *
   * Nên: cờ này `false` → payload BỎ HẲN key `alcohol_curfew` (PATCH merge theo
   * key, bỏ key = giữ nguyên trên server). BE deploy xong, field có mặt, cờ tự
   * thành `true` và mọi thứ chạy đúng hợp đồng mới. Không có gì phải xoá.
   *
   * ⚠️ Đây KHÔNG phải cờ `curfewTouched` cũ (workaround cho chuyện
   * `alcohol_curfew: null` mang ba nghĩa) — đừng dựng lại cờ đó.
   */
  config_available: boolean;
  /** `enabled` theo server. Khác `curfew_enabled` (giá trị người dùng đang sửa). */
  saved_enabled: boolean;
  /** Hôm nay cấu hình có hiệu lực không — backend tự tính. */
  applies_today: boolean;
  /** `applies_today = false` có phải vì cửa hàng bán uống tại chỗ không. */
  suppressed_by_on_premise: boolean;
};

type StoreFormData = {
  code: string;
  name: string;
  address: string;
  /**
   * Múi giờ IANA CỦA CỬA HÀNG — field cấp store, KHÔNG nằm trong
   * `alcohol_curfew`.
   *
   * Backend tính giờ cấm bán rượu theo chuỗi:
   *   `alcohol_curfew.timezone` → `store.timezone` → múi giờ mặc định của hệ
   *   thống.
   * Mắt xích cuối KHÔNG phải giờ Ba Lan, và backend CỐ Ý degrade im lặng thay
   * vì báo lỗi (chuỗi sai không được làm sập cửa hàng). Hệ quả: cửa hàng chưa
   * khai múi giờ vẫn "chạy bình thường" nhưng áp giờ cấm lệch vài tiếng, theo
   * hướng CHO BÁN vào giờ luật cấm — sai lệch có hậu quả pháp lý mà không có
   * bất kỳ dấu hiệu nào trên giao diện.
   *
   * Trước đây form chỉ có ô múi giờ của GIỜ CẤM, nên khi người dùng chọn "dùng
   * múi giờ cửa hàng" họ tưởng đang trỏ về múi giờ thật của cửa hàng — trong
   * khi cửa hàng chưa hề có múi giờ nào và cũng không có ô nào để khai. Đây là
   * ô lấp lỗ hổng đó, và vì vậy nó BẮT BUỘC (xem `validateStoreField`).
   */
  timezone: string;
  contact_phone: string;
  default_language: string;
  business_status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  operating_hours: OperatingHours;
  pricing_zone: string;
  tax_profile_ref: string;
  invoice_profile_ref: string;
  local_backend_url: string;
  partner_server_url: string;
  metadata: string;

  // ── Giờ cấm bán rượu ────────────────────────────────────────────────────────
  // Tất cả nằm trong object `alcohol_curfew` khi gửi lên, TRỪ
  // `consumption_on_premise` — field đó ở cấp store.
  curfew_enabled: boolean;
  curfew_start_time: string;
  curfew_end_time: string;
  curfew_timezone: string;
  // `city`, `legal_basis`, `effective_from`, `effective_to` CỐ TÌNH không có ở
  // đây. Form admin không nhập bốn thứ đó nữa (BE vẫn giữ cột), và cách duy
  // nhất bảo đảm chúng không lọt vào payload là form không hề biết tới chúng —
  // xem `buildCurfewPayload`.
  /** Quán uống tại chỗ — bật lên thì backend không áp giờ cấm nữa. */
  consumption_on_premise: boolean;
  /** Chỉ đọc — xem `CurfewStatus`. KHÔNG gửi lên server. */
  curfew_status: CurfewStatus;
  /**
   * Ảnh cửa hàng (sheet 2 mục #61). BE bật `image_urls` 03/09/2026 trên đủ
   * NĂM serializer, gồm cả Create/Update — tức là ghi được thật, không phải
   * chỉ đọc như đợt trước.
   */
  imageUrls: string[];
  /**
   * Thuế suất VAT mặc định của cửa hàng, phần trăm (sheet 2 #77).
   * Áp cho sản phẩm không khai `tax_rate` riêng.
   */
  defaultTaxRate: string;
  /**
   * Mã tiền tệ của cửa hàng (ISO 4217), vd `UAH`, `PLN`.
   *
   * Field này ĐÃ CÓ ở BE (`Store.currency`) và cả FE cũng đã đọc nó —
   * `useActiveCurrency` lấy đúng `selectedStore.currency` để quyết định ký
   * hiệu tiền cho mọi màn. Nhưng form cửa hàng chưa bao giờ có ô nhập, nên
   * vận hành KHÔNG CÓ CÁCH NÀO đặt giá trị: mọi cửa hàng rơi về `APP_CURRENCY`
   * và chuỗi đa thị trường hiện sai ký hiệu ở khắp nơi (mục #232).
   *
   * Bỏ trống = để BE áp mặc định của nó, không phải "không có tiền tệ".
   */
  currency: string;
};

type StoreFormErrors = Partial<Record<keyof StoreFormData, string>>;
type StoreFormTouched = Partial<Record<keyof StoreFormData, boolean>>;

const formFieldOrder: Array<keyof StoreFormData> = [
  'code',
  'name',
  'address',
  // Ngay sau `address`: ô múi giờ nằm cùng hàng với ô địa chỉ trên giao diện,
  // nên thứ tự focus khi submit lỗi cũng phải theo đúng thứ tự mắt đọc.
  'timezone',
  'contact_phone',
  'default_language',
  'business_status',
  'operating_hours',
  'pricing_zone',
  'tax_profile_ref',
  'invoice_profile_ref',
  'local_backend_url',
  'partner_server_url',
  'metadata',
  'curfew_start_time',
  'curfew_end_time',
];

const emptyStoreForm: StoreFormData = {
  code: '',
  name: '',
  address: '',
  // KHÔNG đặt sẵn `Europe/Warsaw`: đoán hộ múi giờ chính là cách bug này sinh
  // ra. Người dùng phải chọn tường minh.
  timezone: '',
  contact_phone: '',
  default_language: 'pl',
  business_status: 'ACTIVE',
  operating_hours: defaultOperatingHours(),
  pricing_zone: '',
  tax_profile_ref: '',
  invoice_profile_ref: '',
  local_backend_url: '',
  partner_server_url: '',
  metadata: '',
  curfew_enabled: false,
  curfew_start_time: '22:00',
  curfew_end_time: '06:00',
  curfew_timezone: '',
  consumption_on_premise: false,
  // `applies_today: true` là giá trị "KHÔNG CÓ GÌ ĐỂ CẢNH BÁO" cho form trắng,
  // không phải khẳng định hôm nay có giờ cấm. Chú thích chỉ hiện khi
  // `saved_enabled` bật, mà form trắng thì nó tắt.
  //
  // `config_available: true` cho nhánh TẠO MỚI: cửa hàng chưa tồn tại nên không
  // có cấu hình nào trên server để lỡ tay ghi đè — thứ người dùng vừa khai
  // trong form BẮT BUỘC phải được gửi lên. Guard ở `CurfewStatus` chỉ có nghĩa
  // với cửa hàng ĐÃ CÓ, và form của cửa hàng đã có luôn đi qua `toFormData`.
  curfew_status: {
    config_available: true,
    saved_enabled: false,
    applies_today: true,
    suppressed_by_on_premise: false,
  },
  imageUrls: [],
  // Để TRỐNG chứ không đặt sẵn 23: mức mặc định khác nhau theo thị trường
  // (PL 23%, UA 20%) và BE đã biết — đoán hộ ở form là gán sai cho một nửa.
  defaultTaxRate: '',
  // Cùng lý do: KHÔNG điền sẵn `APP_CURRENCY`. Điền sẵn thì người tạo cửa hàng
  // Warszawa bấm Lưu mà không để ý, và cửa hàng đó lặng lẽ thành UAH.
  currency: '',
};

/**
 * Cấu hình rỗng dùng khi CHƯA có dữ liệu server.
 *
 * ⚠️ Đây KHÔNG phải chỗ xử lý "BE chưa trả `alcohol_curfew_config`". Nó chỉ
 * cho form có giá trị để HIỂN THỊ khi thiếu dữ liệu. Phần quan trọng — không
 * gửi nhóm curfew lên server trong tình huống đó — nằm ở
 * `CurfewStatus.config_available`, và cờ ấy đọc `rawConfig` TRƯỚC khi rơi vào
 * fallback này. Đừng gộp hai việc làm một.
 */
const emptyCurfewConfig: AlcoholCurfewConfig = {
  enabled: false,
  start_time: '22:00',
  end_time: '06:00',
  timezone: null,
  city: null,
  legal_basis: null,
  effective_from: null,
  effective_to: null,
  applies_today: true,
  suppressed_by_on_premise: false,
};

function mapStoreStatus(status?: Store['business_status']): FacilityStatus {
  switch (status) {
    case 'ACTIVE':
      return 'ONLINE';
    case 'INACTIVE':
      return 'OFFLINE';
    case 'MAINTENANCE':
      return 'MAINTENANCE';
    default:
      return 'OFFLINE';
  }
}

function mapStoreType(status?: Store['business_status']): FacilityType {
  return status === 'MAINTENANCE' ? 'WAREHOUSE' : 'STORE';
}

function transformStoreToRow(store: Store): StoreTableRow {
  return {
    id: store.id,
    code: store.code,
    name: store.name,
    type: mapStoreType(store.business_status),
    address: store.address,
    city: store.city ?? '-',
    status: mapStoreStatus(store.business_status),
    // Ảnh đầu tiên làm đại diện. `image_urls` là MẢNG (cột ảnh số ít không tồn
    // tại ở BE) và có thể rỗng — cửa hàng chưa gắn ảnh là chuyện thường.
    imageUrl: Array.isArray(store.image_urls) ? store.image_urls[0] ?? null : null,
  };
}

/**
 * Đọc bảng giờ mở cửa từ backend về state của form.
 *
 * QUY ƯỚC ĐÃ CHỐT VỚI BE: ngày nghỉ bị BỎ HẲN KEY khỏi object (xem
 * `serializeOperatingHours` ở dưới và `StoreOperatingHours` trong shared-types;
 * user-app cũng đọc theo đúng quy ước này ở `utils/formatStoreHours.ts`).
 *
 * Vì vậy "thiếu key" = NGHỈ, không phải "dùng mặc định". Bản cũ trả về mặc định
 * (`closed: false`) cho ngày thiếu key, nên tick Off rồi lưu xong mở lại là mất
 * sạch trạng thái Off — đúng triệu chứng BUG #47.
 */
function parseOperatingHours(raw: unknown): OperatingHours {
  const defaults = defaultOperatingHours();
  if (!raw || typeof raw !== 'object') return defaults;
  const src = raw as Record<string, { open?: string; close?: string; closed?: boolean } | null | undefined>;

  // Chỉ suy "thiếu key = nghỉ" khi object THẬT SỰ là bảng giờ theo thứ trong
  // tuần: có ít nhất một key ngày quen thuộc, hoặc rỗng (`{}` = nghỉ cả tuần).
  // Object lạ (BE đổi shape, key viết tắt 'mon'/'tue'...) thì giữ mặc định thay
  // vì đóng cửa nhầm cả bảy ngày.
  const hasKnownDay = DAYS.some((day) => day in src);
  const treatMissingAsClosed = hasKnownDay || Object.keys(src).length === 0;

  const result = { ...defaults };
  for (const day of DAYS) {
    const dayData = src[day];
    if (dayData && typeof dayData === 'object') {
      result[day] = {
        open: dayData.open ?? '08:00',
        close: dayData.close ?? '22:00',
        // BE có khai cờ `closed` trong `StoreDayHours` nên vẫn tôn trọng nếu có.
        closed: !!dayData.closed,
      };
    } else if (treatMissingAsClosed) {
      result[day] = { ...defaults[day], closed: true };
    }
  }
  return result;
}

function toFormData(store: Partial<StoreDetail>): StoreFormData {
  /**
   * Form quản trị đọc `alcohol_curfew_config`, KHÔNG đọc `alcohol_curfew`.
   *
   * `alcohol_curfew` là bản ĐÃ LỌC dành cho app user: nó `null` khi hôm nay
   * ngoài khoảng hiệu lực hoặc khi cửa hàng bán uống tại chỗ, dù cấu hình vẫn
   * còn nguyên trong DB. Dựng form từ đó là dựng từ một chữ `null` mang ba
   * nghĩa — gốc của BUG #46.
   *
   * `alcohol_curfew_config` là cấu hình THÔ, BE cam kết luôn là object, nên đọc
   * thẳng `config.enabled` là ra trạng thái thật. Không còn heuristic
   * "có object tức là đang bật" nữa.
   *
   * Hai trạng thái PHẢI phân biệt, đừng gộp làm một:
   *   - CÓ MẶT (BE mới) → tin hoàn toàn, form đọc từ nó, mỗi lần Lưu đều gửi
   *     nhóm `alcohol_curfew`.
   *   - VẮNG MẶT (`undefined`/`null` — bản BE cũ trên QA) → FE không biết trạng
   *     thái thật, nên tuyệt đối KHÔNG gửi nhóm `alcohol_curfew`. Xem
   *     `CurfewStatus.config_available`.
   */
  const rawConfig = store.alcohol_curfew_config;
  const configAvailable = rawConfig != null;
  /**
   * Kiểu `Partial<>` là CỐ Ý, không phải thừa: BE có thể deploy một phần /
   * serializer thiếu field, và lúc đó `?? emptyCurfewConfig` (fallback cho CẢ
   * object) không đỡ được gì vì object vẫn có mặt, chỉ thiếu vài key.
   *
   * Nguyên tắc chọn mặc định bên dưới: THIẾU DỮ LIỆU THÌ KHÔNG SUY RA KẾT LUẬN
   * TIÊU CỰC. Hiện cảnh báo sai còn tệ hơn không hiện cảnh báo.
   */
  const config: Partial<AlcoholCurfewConfig> = rawConfig ?? emptyCurfewConfig;
  let operatingHours = defaultOperatingHours();
  if (store.operating_hours) {
    if (typeof store.operating_hours === 'string') {
      try { operatingHours = parseOperatingHours(JSON.parse(store.operating_hours)); } catch { /* keep default */ }
    } else {
      operatingHours = parseOperatingHours(store.operating_hours);
    }
  }

  return {
    code: store.code ?? '',
    name: store.name ?? '',
    address: store.address ?? '',
    // Cột trên model là NULLABLE và rất nhiều cửa hàng cũ đang `null` — đọc ra
    // chuỗi rỗng để `<select>` ở trạng thái "chưa chọn", rồi để validation +
    // banner cảnh báo lo phần còn lại. TUYỆT ĐỐI không fallback sang
    // `alcohol_curfew_config.timezone`: hai field khác nghĩa, và lấp liếm ở đây
    // sẽ khiến cửa hàng trông như đã khai múi giờ trong khi thật ra chưa.
    timezone: store.timezone ?? '',
    contact_phone: store.contact_phone ?? '',
    default_language: store.default_language ?? 'pl',
    business_status: (store.business_status ?? 'ACTIVE') as StoreFormData['business_status'],
    operating_hours: operatingHours,
    pricing_zone: store.pricing_zone ?? '',
    tax_profile_ref: store.tax_profile_ref ?? '',
    invoice_profile_ref: store.invoice_profile_ref ?? '',
    local_backend_url: store.local_backend_url ?? '',
    partner_server_url: store.partner_server_url ?? '',
    metadata: store.metadata
      ? (typeof store.metadata === 'object' ? JSON.stringify(store.metadata, null, 2) : String(store.metadata))
      : '',

    // `=== true` chứ không phải `!!`/gán thẳng: thiếu field thì phải ra BOOLEAN
    // THẬT `false`, không được ra `undefined` — `undefined` biến checkbox từ
    // controlled thành uncontrolled và React sẽ cảnh báo.
    curfew_enabled: config.enabled === true,
    curfew_start_time: toTimeInputValue(config.start_time) || '22:00',
    curfew_end_time: toTimeInputValue(config.end_time) || '06:00',
    curfew_timezone: config.timezone ?? '',
    // `config.city` / `legal_basis` / `effective_from` / `effective_to` KHÔNG
    // đọc vào form: form không hiển thị và cũng không gửi lại bốn field đó.
    consumption_on_premise: store.consumption_on_premise ?? false,
    curfew_status: {
      config_available: configAvailable,
      saved_enabled: config.enabled === true,
      // `!== false` (KHÔNG phải `?? false`): thiếu `applies_today` → coi như
      // CÓ hiệu lực, tức KHÔNG hiện banner "cấu hình không hiệu lực hôm nay".
      // Mặc định `false` sẽ dán banner cảnh báo sai lên mọi cửa hàng đang chạy
      // bình thường ngay khi serializer thiếu một field.
      applies_today: config.applies_today !== false,
      suppressed_by_on_premise: config.suppressed_by_on_premise === true,
    },
    // Mảng rỗng = chưa có ảnh, là trạng thái HỢP LỆ (không phải thiếu dữ liệu)
    imageUrls: Array.isArray(store.image_urls) ? store.image_urls : [],
    defaultTaxRate: store.default_tax_rate ?? '',
    currency: (store as { currency?: string | null }).currency ?? '',
  };
}

/** 'HH:MM:SS' hoặc 'HH:MM' -> 'HH:MM' cho <input type="time">. */
function toTimeInputValue(value?: string | null): string {
  if (!value) return '';
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function toApiTime(value: string): string | undefined {
  if (!value) return undefined;
  if (value.length === 5) return `${value}:00`;
  return value;
}

function serializeOperatingHours(oh: OperatingHours): string {
  const result: Record<string, { open: string; close: string }> = {};
  for (const day of DAYS) {
    if (!oh[day].closed) {
      result[day] = { open: oh[day].open, close: oh[day].close };
    }
  }
  return JSON.stringify(result);
}

function toCreatePayload(form: StoreFormData): CreateStoreRequest {
  return {
    // `POST /stores/` GIỜ NHẬN cả nhóm giờ cấm (BE 26/08/2026), nên tạo cửa
    // hàng chỉ còn ĐÚNG MỘT request. Nhánh "tạo xong rồi PATCH thêm một nhịp"
    // trong `handleSubmitForm` đã gỡ — đừng dựng lại.
    //
    // Cùng guard với `toUpdatePayload` (xem `CurfewStatus.config_available`).
    // Ở nhánh tạo mới cờ này LUÔN `true` — form trắng dựng từ `emptyStoreForm`,
    // chưa có cửa hàng nên chưa có gì trên server để ghi đè nhầm.
    ...(form.curfew_status.config_available ? buildCurfewPayload(form) : {}),
    code: form.code,
    name: form.name,
    address: form.address,
    // Field CẤP STORE, KHÔNG nằm trong object `alcohol_curfew`. Luôn gửi:
    // validation đã chặn chuỗi rỗng nên tới đây chắc chắn có giá trị thật.
    timezone: form.timezone.trim(),
    contact_phone: form.contact_phone || undefined,
    default_language: form.default_language || undefined,
    business_status: form.business_status as StoreStatus,
    operating_hours: serializeOperatingHours(form.operating_hours),
    pricing_zone: form.pricing_zone || undefined,
    tax_profile_ref: form.tax_profile_ref || undefined,
    invoice_profile_ref: form.invoice_profile_ref || undefined,
    local_backend_url: form.local_backend_url || undefined,
    partner_server_url: form.partner_server_url || undefined,
    metadata: form.metadata || undefined,
    // Tạo mới chưa chọn ảnh thì bỏ hẳn key, để BE dùng mặc định của nó
    image_urls: form.imageUrls.length > 0 ? form.imageUrls : undefined,
    // Bỏ trống = để BE áp mức mặc định theo thị trường, đừng gửi '' hay 0
    default_tax_rate: form.defaultTaxRate.trim() || undefined,
    currency: form.currency.trim().toUpperCase() || undefined,
  };
}

/**
 * Nhóm field giờ cấm bán rượu — DÙNG CHUNG cho cả `POST` lẫn `PATCH`.
 *
 * Từ 26/08/2026 `StoreCreateSerializer` và `StoreUpdateSerializer` nhận cùng
 * một shape, nên hai luồng dùng đúng một hàm, không còn hai bản logic lệch nhau.
 *
 * ⚠️ CHỈ gửi `alcohol_curfew` (ghi). TUYỆT ĐỐI không nhét
 * `alcohol_curfew_config` vào payload: field đó read-only, BE bỏ qua im lặng —
 * gửi mà tưởng đã lưu là loại lỗi khó thấy nhất.
 *
 * ⚠️ `city`, `legal_basis`, `effective_from`, `effective_to` PHẢI VẮNG MẶT
 * HOÀN TOÀN khỏi object này — không phải `''`, cũng không phải `null`. BE chốt
 * ngữ nghĩa PATCH: gửi `alcohol_curfew` mà THIẾU key nào thì key đó GIỮ NGUYÊN
 * trên server; chỉ `null` TƯỜNG MINH mới xoá. Đây là dữ liệu pháp lý (căn cứ
 * nghị quyết, ngày hiệu lực) do nơi khác nhập, form admin không còn ô nhập nên
 * không có gì để gửi — thêm lại key ở đây là XOÁ dữ liệu thật của người khác.
 */
function buildCurfewPayload(form: StoreFormData): Pick<UpdateStoreRequest, 'consumption_on_premise' | 'alcohol_curfew'> {
  return {
    consumption_on_premise: form.consumption_on_premise,
    alcohol_curfew: form.curfew_enabled
      ? {
          enabled: true,
          start_time: form.curfew_start_time,
          end_time: form.curfew_end_time,
          timezone: form.curfew_timezone.trim() || null,
        }
      /**
       * BỎ TICK = `{ enabled: false }`, KHÔNG PHẢI `null`. Hai thứ khác hẳn
       * nhau ở phía backend:
       *
       *   - `{ enabled: false }` → TẮT nhưng GIỮ NGUYÊN `legal_basis`,
       *     `effective_from`, `city`, giờ đã khai. PATCH merge theo key: key
       *     nào không gửi thì không đụng tới. Bật lại là còn nguyên dữ liệu.
       *   - `null` → XOÁ HẲN cấu hình. Mất luôn căn cứ pháp lý — thứ phải trình
       *     khi bị thanh tra và không dựng lại được từ trí nhớ.
       *
       * Bỏ tick một checkbox không thể mang nghĩa xoá dữ liệu pháp lý, nên form
       * KHÔNG BAO GIỜ gửi `null` ở đây. `null` chỉ dành cho một nút "xoá cấu
       * hình" tường minh — UI hiện KHÔNG có nút đó, và đừng tự thêm.
       */
      : { enabled: false },
  };
}

/**
 * Payload `PATCH /stores/{id}/`.
 *
 * Nhóm giờ cấm GỬI MỖI LẦN LƯU, không cần cờ "người dùng có chỉnh không" nữa.
 *
 * Trước đây phải có cờ `curfewTouched` vì form đọc `alcohol_curfew` — một chữ
 * `null` mang ba nghĩa (chưa bật / quán uống tại chỗ / ngoài khoảng hiệu lực),
 * nên form không phân biệt được và gửi mặc định là biến "vào sửa số điện thoại
 * rồi bấm Lưu" thành "tắt luôn quy định giờ cấm" (BUG #46). Giờ form đọc
 * `alcohol_curfew_config` — luôn là object, `enabled` nói thẳng trạng thái thật
 * — nên thứ gửi lên luôn khớp thứ đang hiển thị, hết lý do phải né.
 *
 * ⚠️ ĐIỀU KIỆN của câu trên: `alcohol_curfew_config` PHẢI có trong response.
 * Chừng nào bản BE mới chưa deploy, field đó vắng mặt và cả đoạn lý luận trên
 * sụp — nên `config_available` chặn nhóm curfew ra khỏi payload. Xem
 * `CurfewStatus.config_available`; guard tự vô hiệu khi BE trả field mới.
 */
function toUpdatePayload(form: StoreFormData): UpdateStoreRequest {
  return {
    // Cờ `false` → BỎ HẲN key `alcohol_curfew` (và `consumption_on_premise`)
    // khỏi payload. PATCH merge theo key: không gửi key = server giữ nguyên.
    // Gửi `{ enabled: false }` ở đây là XOÁ quy định giờ cấm thật.
    ...(form.curfew_status.config_available ? buildCurfewPayload(form) : {}),
    name: form.name,
    address: form.address,
    // Xem `toCreatePayload`: field cấp store, luôn gửi, không bao giờ rỗng.
    timezone: form.timezone.trim(),
    contact_phone: form.contact_phone || undefined,
    default_language: form.default_language || undefined,
    business_status: form.business_status as StoreStatus,
    operating_hours: serializeOperatingHours(form.operating_hours),
    pricing_zone: form.pricing_zone || undefined,
    tax_profile_ref: form.tax_profile_ref || undefined,
    invoice_profile_ref: form.invoice_profile_ref || undefined,
    local_backend_url: form.local_backend_url || undefined,
    partner_server_url: form.partner_server_url || undefined,
    metadata: form.metadata || undefined,
    // Ở PATCH thì mảng rỗng LÀ ý định "xoá hết ảnh" — gửi nguyên, đừng đổi
    // thành `undefined` như nhánh tạo mới (làm vậy là không xoá được ảnh nữa).
    image_urls: form.imageUrls,
    // Ô trống ở đây KHÁC ảnh: nghĩa là "để BE áp mức mặc định theo thị trường",
    // nên gửi `null` tường minh chứ không phải bỏ key (bỏ key = giữ nguyên mức
    // cũ, người dùng xoá ô mà không có gì xảy ra).
    default_tax_rate: form.defaultTaxRate.trim() || null,
    // Giống `default_tax_rate`: gửi `null` tường minh khi để trống, để người
    // dùng gỡ được tiền tệ đã đặt nhầm. Bỏ key = giữ nguyên giá trị cũ.
    currency: form.currency.trim().toUpperCase() || null,
  };
}

/** Bug #209: thông báo validation phải đi qua i18n — nhận `t` thay vì chuỗi cứng. */
function validateStoreField(
  field: keyof StoreFormData,
  form: StoreFormData,
  mode: ModalMode,
  t: (key: string) => string
): string | undefined {
  if (field === 'code' && mode !== 'edit' && !form.code.trim()) return t('validationCodeRequired');
  if (field === 'name' && !form.name.trim()) return t('validationNameRequired');
  if (field === 'address' && !form.address.trim()) return t('validationAddressRequired');

  /**
   * Múi giờ BẮT BUỘC ở CẢ `add` LẪN `edit` — cố ý không nới cho `edit`.
   *
   * Rất nhiều cửa hàng cũ đang `timezone = null`, nên chỉ mở Edit để sửa số
   * điện thoại cũng dính lỗi này. Đó là CHỦ Ý: cấu hình đang tính giờ cấm bán
   * rượu sai múi giờ thì không được phép lưu tiếp mà vẫn để nguyên chỗ sai.
   * Đổi lại, form phải GIẢI THÍCH chứ không chỉ gắn chữ đỏ "bắt buộc" — xem
   * banner `storeTimezoneMissingWarning` ở đầu modal.
   */
  if (field === 'timezone' && !form.timezone.trim()) return t('validationTimezoneRequired');

  // Giờ cấm — chỉ kiểm khi đang bật, tắt rồi thì mấy ô kia không có nghĩa gì.
  if (form.curfew_enabled) {
    if (field === 'curfew_start_time' && !form.curfew_start_time) return t('validationStartTimeRequired');
    if (field === 'curfew_end_time') {
      if (!form.curfew_end_time) return t('validationEndTimeRequired');
      // Bằng nhau = khung giờ dài 0 phút, tức là không cấm gì cả — nhưng nhìn
      // vào cấu hình lại tưởng đang cấm. Chặn ở đây thay vì để nó âm thầm sai.
      if (form.curfew_end_time === form.curfew_start_time) return t('validationEndTimeDiffersFromStart');
    }
  }

  // URL validation
  if (field === 'local_backend_url' && form.local_backend_url.trim()) {
    try {
      new URL(form.local_backend_url);
    } catch {
      return t('validationInvalidUrlFormat');
    }
  }
  if (field === 'partner_server_url' && form.partner_server_url.trim()) {
    try {
      new URL(form.partner_server_url);
    } catch {
      return t('validationInvalidUrlFormat');
    }
  }

  return undefined;
}

function validateStoreForm(
  form: StoreFormData,
  mode: ModalMode,
  t: (key: string) => string
): StoreFormErrors {
  const errors: StoreFormErrors = {};

  for (const field of formFieldOrder) {
    const message = validateStoreField(field, form, mode, t);
    if (message) {
      errors[field] = message;
    }
  }

  return errors;
}

// ============================================
// TimeInput — 24h text input with clock icon
// ============================================
/** Bề rộng tối thiểu của bảng chọn giờ. Dùng lại khi kẹp viewport lúc chưa đo được. */
const TIME_POPUP_MIN_WIDTH = 180;
/** Lề tối thiểu giữa popup và mép màn hình. */
const TIME_POPUP_MARGIN = 8;
/** Khoảng hở giữa popup và ô input. */
const TIME_POPUP_GAP = 4;
/**
 * Cao hơn modal Store (`z-50`) và hộp xác nhận xoá (`z-[60]`), nhưng vẫn thấp
 * hơn Toast / dropdown của TopNavBar (`z-[9999]`) để thông báo không bị che.
 */
const TIME_POPUP_Z_INDEX = 1000;

function TimeInput({
  value, defaultValue, id, disabled, onChange,
}: {
  value?: string; defaultValue?: string; id?: string;
  disabled?: boolean; onChange?: (v: string) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * Bảng chọn giờ nằm trong portal ở `document.body`, KHÔNG phải con của
   * `containerRef` trong cây DOM. Cần ref riêng để (a) đo chiều cao thật khi
   * quyết định lật lên/xuống và (b) không coi cú bấm chọn giờ là click ra ngoài.
   */
  const popupRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const current = value ?? defaultValue ?? '08:00';
  const [hh, mm] = current.split(':');

  /**
   * Tính lại vị trí `fixed` của popup theo `getBoundingClientRect()` của ô input.
   *
   * Chiều cao được ĐO từ popup đã render (nội dung có tiêu đề đã dịch nên cao
   * thấp khác nhau theo ngôn ngữ), không hardcode.
   */
  const updatePosition = useCallback(() => {
    const anchor = containerRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Ô input đã bị cuộn ra khỏi vùng nhìn thấy -> đóng, tránh popup lơ lửng.
    if (rect.bottom <= 0 || rect.top >= vh || rect.right <= 0 || rect.left >= vw) {
      setOpen(false);
      return;
    }

    const popup = popupRef.current;
    const height = popup?.offsetHeight ?? 0;
    const width = popup?.offsetWidth ?? TIME_POPUP_MIN_WIDTH;

    // Lật lên trên khi bên dưới không đủ chỗ mà bên trên thì rộng hơn.
    const spaceBelow = vh - rect.bottom - TIME_POPUP_MARGIN;
    const spaceAbove = rect.top - TIME_POPUP_MARGIN;
    const flipUp = height > 0 && spaceBelow < height + TIME_POPUP_GAP && spaceAbove > spaceBelow;

    let top = flipUp ? rect.top - TIME_POPUP_GAP - height : rect.bottom + TIME_POPUP_GAP;
    top = Math.min(top, vh - height - TIME_POPUP_MARGIN);
    top = Math.max(TIME_POPUP_MARGIN, top);

    // Kẹp ngang trong viewport (ô giờ sát mép phải thì popup sẽ tràn).
    let left = Math.min(rect.left, vw - width - TIME_POPUP_MARGIN);
    left = Math.max(TIME_POPUP_MARGIN, left);

    setCoords((prev) =>
      prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5
        ? prev
        : { top, left }
    );
  }, []);

  /**
   * Đo + đặt vị trí TRƯỚC khi trình duyệt vẽ (tránh nháy một frame ở sai chỗ),
   * rồi bám theo mọi lần cuộn/đổi kích thước.
   *
   * `capture: true` là bắt buộc: sự kiện `scroll` không nổi bọt, nên chỉ ở pha
   * capture mới nghe được cuộn của khối Operating Hours và của thân modal.
   */
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Close on outside click (tính cả nội dung nằm trong portal) + phím Escape
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // Ô bị disable trong lúc popup đang mở (tick "Off") -> đóng lại.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const setHour = (h: number) => {
    const newVal = `${String(h).padStart(2, '0')}:${mm ?? '00'}`;
    onChange?.(newVal);
  };
  const setMinute = (m: number) => {
    const newVal = `${hh ?? '08'}:${String(m).padStart(2, '0')}`;
    onChange?.(newVal);
  };

  const normalize = (v: string) => {
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    return v;
  };

  return (
    <div ref={containerRef} className={`relative ${disabled ? 'opacity-40' : ''}`}>
      <div className="flex items-center h-6 w-[68px] rounded border border-slate-200 bg-white overflow-hidden focus-within:border-blue-400">
        <input
          id={id}
          type="text"
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          placeholder="HH:MM"
          maxLength={5}
          onChange={(e) => onChange?.(normalize(e.target.value))}
          className="h-full w-full pl-1.5 pr-0 text-xs bg-transparent outline-none disabled:bg-slate-50"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="px-1 text-slate-400 hover:text-blue-500 transition-colors shrink-0"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {open && createPortal(
        <div
          ref={popupRef}
          className="rounded-lg border border-slate-200 bg-white shadow-xl text-xs p-2"
          style={{
            position: 'fixed',
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            // Render ẩn ở frame đo đạc đầu tiên: `visibility: hidden` vẫn giữ
            // layout nên đo được chiều cao thật, mà không lộ ra ở sai chỗ.
            visibility: coords ? 'visible' : 'hidden',
            zIndex: TIME_POPUP_Z_INDEX,
            minWidth: TIME_POPUP_MIN_WIDTH,
          }}
        >
          {/* Hours grid 6x4 */}
          <div className="mb-1.5">
            <div className="text-[10px] text-slate-400 mb-1 font-medium">{t('hour')}</div>
            <div className="grid grid-cols-6 gap-0.5">
              {Array.from({ length: 24 }, (_, i) => (
                <button key={i} type="button"
                  onClick={() => setHour(i)}
                  className={`rounded py-0.5 text-center text-xs transition-colors hover:bg-blue-50 hover:text-blue-600 ${parseInt(hh) === i ? 'bg-blue-500 text-white font-semibold' : 'text-slate-700'}`}
                >
                  {String(i).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
          {/* Minutes grid 6x2 */}
          <div>
            <div className="text-[10px] text-slate-400 mb-1 font-medium">{t('minute')}</div>
            <div className="grid grid-cols-6 gap-0.5">
              {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                <button key={m} type="button"
                  onClick={() => { setMinute(m); setOpen(false); }}
                  className={`rounded py-0.5 text-center text-xs transition-colors hover:bg-blue-50 hover:text-blue-600 ${parseInt(mm) === m ? 'bg-blue-500 text-white font-semibold' : 'text-slate-700'}`}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function StoresPage() {
  const { t } = useI18n();
  const isAdmin = useIsAdmin();
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formData, setFormData] = useState<StoreFormData>(emptyStoreForm);
  const [formErrors, setFormErrors] = useState<StoreFormErrors>({});
  const [formTouched, setFormTouched] = useState<StoreFormTouched>({});
  const fieldRefs = useRef<Partial<Record<keyof StoreFormData, HTMLInputElement | HTMLSelectElement | null>>>({});
  const detailAppliedRef = useRef<string | null>(null); // tracks which storeId's detail has been applied
  /**
   * Người dùng đã chỉnh gì trong lần mở modal này chưa.
   *
   * Cờ này quyết định dữ liệu từ server có được phép ghi đè form hay không —
   * xem effect nạp `storeDetail` ở dưới. Dùng ref chứ không dùng state vì nó chỉ
   * để đọc bên trong effect, không cần render lại.
   */
  const formDirtyRef = useRef(false);
  const [ohOpen, setOhOpen] = useState(false); // operating hours dropdown open state
  /**
   * Giờ ở dòng "All" của bảng Operating Hours.
   *
   * Phải là state có kiểm soát: `TimeInput` chỉ báo giá trị mới qua `onChange`,
   * bảng chọn giờ/phút KHÔNG ghi vào DOM. Bản cũ để dòng này không kiểm soát
   * (`defaultValue`, không `onChange`) rồi đọc ngược bằng
   * `document.getElementById(...).value` — bấm chọn giờ trong bảng thì không có
   * gì xảy ra vì `onChange` là `undefined`, đúng triệu chứng BUG #45.
   */
  const [applyHours, setApplyHours] = useState<{ open: string; close: string }>({ open: '08:00', close: '22:00' });
  const pageSize = 15;

  const { triggerExport, isExporting, progress: exportProgress } = useExport({ endpoint: '/stores/', filePrefix: 'stores' });

  const { data: storesData, isLoading, error } = useStores({ page: currentPage, page_size: pageSize });
  const { data: statsData } = useStoresStats();
  const deleteStore = useDeleteStore();
  const createStore = useCreateStore();
  const updateStore = useUpdateStore();

  const { data: storeDetailData, isLoading: isLoadingDetail, error: storeDetailError } = useStore(activeStoreId ?? '', {
    enabled: !!activeStoreId && (modalMode === 'view' || modalMode === 'edit'),
  });

  const storesList: Store[] = useMemo(() => {
    const data = storesData as
      | {
        results?: Store[];
        items?: Store[];
        data?: Store[] | { results?: Store[]; items?: Store[] };
        meta?: { count?: number };
      }
      | undefined;
    console.log('[StoresList] raw storesData:', storesData);
    if (Array.isArray(data?.data)) return data.data;
    if (data?.data && typeof data.data === 'object') {
      return data.data.results ?? data.data.items ?? [];
    }
    return data?.results ?? data?.items ?? [];
  }, [storesData]);

  const storesCount = useMemo(() => {
    const data = storesData as
      | { count?: number; total?: number; meta?: { count?: number }; data?: Store[] }
      | undefined;
    return data?.count ?? data?.total ?? data?.meta?.count ?? storesList.length;
  }, [storesData, storesList.length]);

  const tableData: StoreTableRow[] = useMemo(() => storesList.map(transformStoreToRow), [storesList]);
  const totalStores = storesCount;

  const storesCreatedThisMonth = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11

    return storesList.filter((store) => {
      if (!store.created_at) return false;
      const createdAt = new Date(store.created_at);
      return (
        createdAt.getFullYear() === currentYear &&
        createdAt.getMonth() === currentMonth
      );
    }).length;
  }, [storesList]);

  const activeStoreFromList = useMemo(
    () => storesList.find((store) => store.id === activeStoreId) ?? null,
    [storesList, activeStoreId]
  );

  const confirmStore = useMemo(() => {
    if (!deleteConfirmId) return null;
    return storesList.find((s) => s.id === deleteConfirmId) ?? null;
  }, [storesList, deleteConfirmId]);

  const isActivating = useMemo(() => {
    if (!confirmStore) return false;
    return confirmStore.business_status === 'INACTIVE';
  }, [confirmStore]);

  const storeDetail = useMemo(() => {
    const raw = storeDetailData as StoreDetail | { data?: StoreDetail } | undefined;
    if (!raw) return null;
    if ('data' in raw && raw.data) {
      console.log('[StoreDetail] unwrapped from data:', raw.data);
      return raw.data;
    }
    console.log('[StoreDetail] raw:', raw);
    return raw as StoreDetail;
  }, [storeDetailData]);

  // When detail fails (404/403), fall back to list item
  useEffect(() => {
    if (storeDetailError && activeStoreId && activeStoreFromList) {
      // Danh sách cũng được refetch nền (invalidate sau mutation, refetch khi
      // focus lại cửa sổ) và mỗi lần như vậy `activeStoreFromList` đổi tham
      // chiếu — ghi đè lại form là xoá sạch thứ người dùng vừa gõ/tick.
      if (formDirtyRef.current) return;
      console.warn('[StoreDetail] error fetching detail, using list item:', storeDetailError);
      setFormData(toFormData(activeStoreFromList));
      detailAppliedRef.current = activeStoreId;
    }
  }, [storeDetailError, activeStoreId, activeStoreFromList]);

  useEffect(() => {
    if (!modalMode) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [modalMode]);

  useEffect(() => {
    if (modalMode === 'create') {
      // Chỉ dựng form trắng khi chưa gõ gì — effect này còn phụ thuộc vào dữ
      // liệu query, đừng để một lần refetch nền xoá trắng form đang nhập dở.
      if (!formDirtyRef.current) {
        setFormData(emptyStoreForm);
        detailAppliedRef.current = null;
      }
      return;
    }

    if ((modalMode === 'edit' || modalMode === 'view') && activeStoreId) {
      if (storeDetail) {
        /**
         * Nạp dữ liệu server vào form CHỪNG NÀO người dùng chưa chỉnh gì.
         *
         * Hai ràng buộc kéo ngược nhau, phải thoả cả hai:
         *
         *  1. PHẢI nhận dữ liệu mới nhất. Mở lại Edit ngay sau khi lưu thì
         *     React Query trả bản cache cũ (đã bị đánh dấu stale) NGAY trong
         *     render đầu rồi mới refetch nền. Bỏ qua kết quả refetch là các
         *     checkbox hiện đúng giá trị TRƯỚC khi lưu — chính triệu chứng
         *     BUG #46/#47.
         *  2. KHÔNG được đè lên thứ người dùng đang gõ. Bản cũ ghi đè form mỗi
         *     lần `storeDetail` đổi tham chiếu, mà refetch nền thì xảy ra liên
         *     tục (mặc định `refetchOnWindowFocus`, cộng các lần invalidate sau
         *     mutation): tick checkbox xong click ra ngoài cửa sổ rồi quay lại
         *     là form âm thầm reset về dữ liệu server.
         *
         * `formDirtyRef` là ranh giới giữa hai điều đó, và được reset mỗi lần
         * mở modal.
         */
        if (!formDirtyRef.current) {
          setFormData(toFormData(storeDetail));
          detailAppliedRef.current = activeStoreId;
        }
      } else if (!detailAppliedRef.current && activeStoreFromList) {
        // Only seed from list item if detail hasn't been applied yet
        setFormData(toFormData(activeStoreFromList));
      }
    }
  }, [modalMode, activeStoreId, storeDetail, activeStoreFromList]);

  const closeModal = () => {
    setModalMode(null);
    setActiveStoreId(null);
    setFormErrors({});
    setFormTouched({});
    setOhOpen(false);
    setApplyHours({ open: '08:00', close: '22:00' });
    detailAppliedRef.current = null;
    formDirtyRef.current = false;
  };

  const handleAddFacility = useCallback(() => {
    if (!isAdmin) return;
    setModalMode('create');
    setActiveStoreId(null);
    setFormData(emptyStoreForm);
    setFormErrors({});
    setFormTouched({});
    setApplyHours({ open: '08:00', close: '22:00' });
    detailAppliedRef.current = null;
    formDirtyRef.current = false;
  }, [isAdmin]);

  const handleView = useCallback((storeId: string) => {
    if (!isAdmin) return;
    const seed = storesList.find((store) => store.id === storeId);
    if (seed) {
      setFormData(toFormData(seed));
    }
    detailAppliedRef.current = null;
    formDirtyRef.current = false;
    setActiveStoreId(storeId);
    setModalMode('view');
  }, [isAdmin, storesList]);

  const handleEdit = useCallback((storeId: string) => {
    if (!isAdmin) return;
    const seed = storesList.find((store) => store.id === storeId);
    if (seed) {
      setFormData(toFormData(seed));
    }
    setFormErrors({});
    setFormTouched({});
    setApplyHours({ open: '08:00', close: '22:00' });
    // Mở lại form = bắt đầu lại từ dữ liệu server, kể cả khi lần trước vừa gõ dở.
    detailAppliedRef.current = null;
    formDirtyRef.current = false;
    setActiveStoreId(storeId);
    setModalMode('edit');
  }, [isAdmin, storesList]);

  const validateAndSetFieldError = useCallback((field: keyof StoreFormData, nextForm: StoreFormData) => {
    const message = validateStoreField(field, nextForm, modalMode, t);
    setFormErrors((prev) => ({
      ...prev,
      [field]: message,
    }));
  }, [modalMode, t]);

  const handleFieldBlur = useCallback((field: keyof StoreFormData) => {
    setFormTouched((prev) => ({ ...prev, [field]: true }));
    validateAndSetFieldError(field, formData);
  }, [formData, validateAndSetFieldError]);

  const handleFieldChange = useCallback((field: keyof StoreFormData, value: string) => {
    // Từ đây trở đi dữ liệu refetch nền không được phép ghi đè form nữa.
    formDirtyRef.current = true;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (formTouched[field]) {
        validateAndSetFieldError(field, next);
      }
      return next;
    });
  }, [formTouched, validateAndSetFieldError]);

  /**
   * Đổi một field của khối giờ cấm.
   *
   * Tách riêng khỏi `handleFieldChange` vì khối này có cả checkbox
   * (`handleFieldChange` chỉ nhận string) và vì hai ô giờ ràng buộc lẫn nhau
   * nên phải validate cả cụm.
   *
   * KHÔNG còn cờ `curfewTouched`: payload gửi nhóm giờ cấm mỗi lần lưu, xem
   * `toUpdatePayload`.
   */
  const handleCurfewChange = useCallback(
    <K extends keyof StoreFormData>(field: K, value: StoreFormData[K]) => {
      formDirtyRef.current = true;
      setFormData((prev) => {
        const next = { ...prev, [field]: value };
        // Giờ bắt đầu/kết thúc ràng buộc lẫn nhau (không được trùng nhau), nên
        // tính lại cả cụm chứ không chỉ ô vừa gõ.
        validateAndSetFieldError('curfew_start_time', next);
        validateAndSetFieldError('curfew_end_time', next);
        return next;
      });
      setFormTouched((prev) => ({
        ...prev,
        curfew_start_time: true,
        curfew_end_time: true,
      }));
    },
    [validateAndSetFieldError]
  );

  const handleDelete = useCallback((storeId: string) => {
    if (!isAdmin) return;
    setDeleteConfirmId(storeId);
  }, [isAdmin]);

  const handleSubmitForm = async () => {
    if (!isAdmin) return;
    const errors = validateStoreForm(formData, modalMode, t);
    setFormErrors(errors);
    setFormTouched(formFieldOrder.reduce<StoreFormTouched>((acc, key) => ({ ...acc, [key]: true }), {}));
    const firstInvalidField = formFieldOrder.find((field) => errors[field]);
    if (firstInvalidField) {
      fieldRefs.current[firstInvalidField]?.focus();
      return;
    }

    try {
      if (modalMode === 'create') {
        /**
         * MỘT request duy nhất. `POST /stores/` đã nhận `alcohol_curfew` +
         * `consumption_on_premise` (BE 26/08/2026), nên nhánh "tạo xong rồi
         * PATCH bù phần giờ cấm" đã gỡ — cùng với nó là trạng thái nửa vời
         * "cửa hàng tạo rồi nhưng giờ cấm thì chưa".
         */
        await createStore.mutateAsync(toCreatePayload(formData));
        closeModal();
        return;
      }

      if (modalMode === 'edit' && activeStoreId) {
        await updateStore.mutateAsync({
          id: activeStoreId,
          data: toUpdatePayload(formData),
        });
        closeModal();
      }
    } catch (err) {
      let message = t('failedToSaveFacility');
      if (err && typeof err === 'object') {
        const e = err as {
          message?: string;
          details?: Array<{ field: string; message: string }>;
          originalError?: { response?: { data?: { error?: { message?: string }; message?: string; errors?: Record<string, string[]> } } };
        };
        if (e.details && Array.isArray(e.details) && e.details.length > 0) {
          message = e.details.map((d) => `${d.field}: ${d.message}`).join('\n');
        } else if (e.message && !e.message.startsWith('Request failed')) {
          message = e.message;
        } else {
          const raw = e.originalError?.response?.data;
          if (raw?.error?.message) message = raw.error.message;
          else if (raw?.message) message = raw.message;
          else if (raw?.errors) {
            message = Object.entries(raw.errors)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('\n');
          } else if (e.message) message = e.message;
        }
      }
      showToast(message, 'error');
    }
  };

  const isSubmitting = createStore.isPending || updateStore.isPending;

  /**
   * Vì sao cấu hình giờ cấm ĐANG BẬT mà hôm nay không có hiệu lực — `null` là
   * không có gì để giải thích.
   *
   * Đây chính là thứ trước đây khiến người dùng tưởng mất cấu hình: form hiện
   * trống trơn (vì `alcohol_curfew` về `null`) mà không nói lý do, nên họ khai
   * lại từ đầu. Giờ cấu hình vẫn hiện đủ, kèm một dòng nói rõ vì sao chưa áp.
   *
   * Đọc `saved_enabled` (trạng thái đã lưu) chứ KHÔNG đọc `curfew_enabled`
   * (trạng thái đang sửa): người dùng vừa tick bật một cấu hình chưa lưu thì
   * `applies_today` của server còn là `false`, hiện cảnh báo lúc đó là báo sai.
   */
  const curfewInactiveReason: 'on_premise' | 'out_of_range' | null =
    formData.curfew_status.saved_enabled && !formData.curfew_status.applies_today
      ? (formData.curfew_status.suppressed_by_on_premise ? 'on_premise' : 'out_of_range')
      : null;

  /**
   * Option phải chèn thêm cho ô múi giờ của GIỜ CẤM khi server đang lưu một giá
   * trị ngoài danh sách dựng sẵn. Xem `getCustomTimezoneOption`.
   */
  const currentCurfewTimezoneOption = useMemo(
    () => getCustomTimezoneOption(formData.curfew_timezone),
    [formData.curfew_timezone]
  );

  /** Cùng việc đó cho ô múi giờ CẤP CỬA HÀNG — nơi hay gặp `Asia/Ho_Chi_Minh` nhất. */
  const currentStoreTimezoneOption = useMemo(
    () => getCustomTimezoneOption(formData.timezone),
    [formData.timezone]
  );

  /**
   * Cửa hàng ĐANG SỬA chưa hề khai múi giờ.
   *
   * Chỉ xét ở chế độ `edit`: form tạo mới thì ô rỗng là chuyện đương nhiên, dán
   * banner cảnh báo lên đó chỉ tạo tiếng ồn. Ở `edit` thì ngược lại — ô rỗng
   * nghĩa là cửa hàng NÀY đang chạy với giờ cấm tính sai múi giờ, và người dùng
   * xứng đáng biết vì sao tự dưng không lưu tiếp được.
   */
  const showMissingTimezoneWarning = modalMode === 'edit' && !formData.timezone.trim();

  /**
   * Phương thức xác minh tuổi của cửa hàng đang mở (mục #62) — CHỈ ĐỌC.
   *
   * Đọc từ bản chi tiết trước, rơi về bản trong danh sách. Mảng rỗng nghĩa là
   * BE chưa trả field (bản cũ) chứ KHÔNG phải "cửa hàng không xác minh tuổi" —
   * BE cam kết không bao giờ trả rỗng, market lạ thì rơi về bộ UA. Vì vậy rỗng
   * thì ẩn hẳn khối này thay vì hiện một danh sách trống gây hiểu nhầm.
   */
  const ageVerificationMethods = useMemo(() => {
    const detail = storeDetail as { age_verification_methods?: string[] } | undefined;
    const fromList = activeStoreFromList as { age_verification_methods?: string[] } | undefined;
    return detail?.age_verification_methods ?? fromList?.age_verification_methods ?? [];
  }, [storeDetail, activeStoreFromList]);

  return (
    <PageContainer>
      <PageHeader
        title={t('storeAddress')}
        subtitle={t('manageStoresAndWarehouses')}
        actions={
          isAdmin ? (
            <div className="flex items-center gap-2">
              <ExportButton onExport={triggerExport} isLoading={isExporting} progress={exportProgress} />
              <ActionButton variant="primary" icon={<PlusIcon size={16} />} onClick={handleAddFacility}>
                {t('storesAddStore')}
              </ActionButton>
            </div>
          ) : <ExportButton onExport={triggerExport} isLoading={isExporting} progress={exportProgress} />
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadFacilities')}</p>
          <p className="mt-1 text-red-600">{error instanceof Error ? error.message : 'An error occurred while loading facilities.'}</p>
        </div>
      )}

      <div className="mt-6">
        <StoreStatsRow
          totalStores={statsData?.total ?? storesList.filter(s => s.business_status !== 'MAINTENANCE').length}
          warehouses={statsData?.warehouses ?? storesList.filter(s => s.business_status === 'MAINTENANCE').length}
          capacityUtilization={statsData?.capacity_utilization ?? 78} // Fallback 78% nếu API trả về null
          isLoading={isLoading}
        />
      </div>

      <div className="mt-6">
        <NodeDirectoryTable
          data={tableData}
          totalCount={storesCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onRowClick={isAdmin ? handleView : () => { }}
          onEdit={isAdmin ? handleEdit : undefined}
          onDelete={isAdmin ? handleDelete : undefined}
          isLoading={isLoading}
        />
      </div>

      {modalMode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.45)' }}
          onClick={closeModal}
        >
          <div
            className="w-full max-w-[720px] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {modalMode === 'view' ? t('storeDetails') : modalMode === 'edit' ? t('editStore') : t('addStore')}
              </h3>
              <button
                onClick={closeModal}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 pb-2 overflow-y-auto flex-1">
              {(isLoadingDetail && modalMode !== 'create' && !activeStoreFromList) ? (
                <div className="py-8 text-center text-sm text-slate-500">{t('loading')}</div>
              ) : (
                <div className="space-y-4">
                  {/**
                    * Cửa hàng cũ chưa khai múi giờ: nói RÕ hậu quả, đừng bắt
                    * người dùng tự suy từ dòng chữ đỏ "Trường bắt buộc" bên
                    * dưới ô chọn.
                    *
                    * Câu chữ hướng tới người vận hành cửa hàng: nói về việc
                    * "giờ cấm bán rượu đang tính sai giờ", KHÔNG nhắc tên biến
                    * cấu hình hay múi giờ mặc định của hệ thống — đó là chuyện
                    * nội bộ của backend, người đứng quầy không làm gì được với
                    * thông tin ấy.
                    */}
                  {showMissingTimezoneWarning && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                      {t('storeTimezoneMissingWarning')}
                    </div>
                  )}

                  {/* Row 1: Store Code + Store Name */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('storeCode')}</label>
                      {(modalMode === 'view' || modalMode === 'edit') ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">{formData.code || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.code = el; }}
                          value={formData.code}
                          onChange={(e) => handleFieldChange('code', e.target.value)}
                          onBlur={() => handleFieldBlur('code')}
                          placeholder={t('enterStoreCode')}
                          className={`h-10 w-full rounded-lg border px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none border-slate-300 ${formErrors.code && formTouched.code ? 'border-red-300 bg-red-50' : ''}`}
                        />
                      )}
                      {formErrors.code && formTouched.code && <p className="mt-1 text-xs text-red-600">{formErrors.code}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('storeName')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.name || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.name = el; }}
                          value={formData.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                          onBlur={() => handleFieldBlur('name')}
                          placeholder={t('enterStoreName')}
                          className={`h-10 w-full rounded-lg border px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none border-slate-300 ${formErrors.name && formTouched.name ? 'border-red-300 bg-red-50' : ''}`}
                        />
                      )}
                      {formErrors.name && formTouched.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
                    </div>
                  </div>

                  {/* Xác minh tuổi (mục #62) — CHỈ ĐỌC ở mọi chế độ.
                      PM chốt 05/09: suy từ `market`, không cho từng cửa hàng
                      chọn, vì đây là ràng buộc pháp lý theo thị trường chứ
                      không phải tuỳ chọn vận hành. Hiện ra để người vận hành
                      biết cửa hàng này chấp nhận giấy tờ gì mà không phải tra
                      tài liệu — và để thấy ngay khi `market` khai sai. */}
                  {modalMode !== 'create' && ageVerificationMethods.length > 0 && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {t('storeAgeVerificationMethods')}
                      </label>
                      <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                        {ageVerificationMethods.map((m) => (
                          <span
                            key={m}
                            className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {t('storeAgeVerificationMethodsHint')}
                      </p>
                    </div>
                  )}

                  {/* Row 2: Type */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('type')}</label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Store option */}
                      <button
                        type="button"
                        onClick={() => modalMode !== 'view' && handleFieldChange('business_status', 'ACTIVE')}
                        style={formData.business_status !== 'MAINTENANCE' ? { borderColor: '#0288D1', backgroundColor: '#E1F5FE', color: '#0288D1' } : {}}
                        className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${formData.business_status !== 'MAINTENANCE' ? '' : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                          <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        </svg>
                        {t('storeType')}
                        <div
                          style={formData.business_status !== 'MAINTENANCE' ? { borderColor: '#0288D1' } : {}}
                          className={`ml-auto h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${formData.business_status !== 'MAINTENANCE' ? '' : 'border-slate-300'}`}
                        >
                          {formData.business_status !== 'MAINTENANCE' && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#0288D1' }} />}
                        </div>
                      </button>
                      {/* Warehouse option */}
                      <button
                        type="button"
                        onClick={() => modalMode !== 'view' && handleFieldChange('business_status', 'MAINTENANCE')}
                        style={formData.business_status === 'MAINTENANCE' ? { borderColor: '#0288D1', backgroundColor: '#E1F5FE', color: '#0288D1' } : {}}
                        className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${formData.business_status === 'MAINTENANCE' ? '' : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M1 3h22v5H1zM1 8h22v13H1z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                          <path d="M8 21V11h8v10" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        </svg>
                        {t('warehouse')}
                        <div
                          style={formData.business_status === 'MAINTENANCE' ? { borderColor: '#0288D1' } : {}}
                          className={`ml-auto h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${formData.business_status === 'MAINTENANCE' ? '' : 'border-slate-300'}`}
                        >
                          {formData.business_status === 'MAINTENANCE' && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#0288D1' }} />}
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Row 3: Address + Store Time Zone.
                      Múi giờ là thuộc tính ĐỊA LÝ của cửa hàng nên đứng cạnh
                      địa chỉ, KHÔNG nằm trong khối "Giờ cấm bán rượu": đặt
                      trong đó thì nó lại trông như một tuỳ chọn của quy định
                      giờ cấm, đúng cái hiểu nhầm sinh ra bug này. */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('address')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.address || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.address = el; }}
                          value={formData.address}
                          onChange={(e) => handleFieldChange('address', e.target.value)}
                          onBlur={() => handleFieldBlur('address')}
                          placeholder={t('enterAddress')}
                          className={`h-10 w-full rounded-lg border px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none border-slate-300 ${formErrors.address && formTouched.address ? 'border-red-300 bg-red-50' : ''}`}
                        />
                      )}
                      {formErrors.address && formTouched.address && <p className="mt-1 text-xs text-red-600">{formErrors.address}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {t('storeTimezone')} <span className="text-red-500">*</span>
                      </label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                          {formData.timezone ? formatTimezoneLabel(formData.timezone) : '-'}
                        </div>
                      ) : (
                        <select
                          ref={(el) => { fieldRefs.current.timezone = el; }}
                          value={formData.timezone}
                          onChange={(e) => handleFieldChange('timezone', e.target.value)}
                          onBlur={() => handleFieldBlur('timezone')}
                          className={`h-10 w-full rounded-lg border px-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none ${formErrors.timezone && formTouched.timezone ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                        >
                          {/**
                            * KHÁC hẳn ô múi giờ của giờ cấm: ở ĐÂY option rỗng
                            * KHÔNG phải lựa chọn hợp lệ, nó chỉ là placeholder
                            * `disabled`.
                            *
                            * Ô giờ cấm để rỗng có nghĩa rõ ràng — "kế thừa múi
                            * giờ cửa hàng". Còn cửa hàng để rỗng thì kế thừa
                            * cái gì? Không có mặc định nào đúng để rơi về, và
                            * chính chỗ rơi vô hình đó là gốc của bug giờ cấm
                            * lệch múi giờ. Nên ô này bắt buộc chọn.
                            */}
                          <option value="" disabled>{t('storeTimezonePlaceholder')}</option>
                          {/* Giá trị server đang lưu nhưng ngoài danh sách —
                              phải có option riêng, nếu không select tự nhảy về
                              option đầu và lần Lưu kế tiếp đổi múi giờ âm thầm. */}
                          {currentStoreTimezoneOption && (
                            <option value={currentStoreTimezoneOption.value}>
                              {t('curfewTimezoneCurrentSaved', { value: currentStoreTimezoneOption.label })}
                            </option>
                          )}
                          {EUROPEAN_TIMEZONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      )}
                      {formErrors.timezone && formTouched.timezone && <p className="mt-1 text-xs text-red-600">{formErrors.timezone}</p>}
                    </div>
                  </div>

                  {/* Row 4: Contact Phone + Default Language */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('contactPhone')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.contact_phone || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.contact_phone = el; }}
                          value={formData.contact_phone}
                          onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                          placeholder={t('enterContactPhone')}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                        />
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('defaultLanguage')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.default_language || '-'}</div>
                      ) : (
                        <select
                          ref={(el) => { fieldRefs.current.default_language = el as any; }}
                          value={formData.default_language}
                          onChange={(e) => handleFieldChange('default_language', e.target.value)}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none"
                        >
                          <option value="">{t('selectLanguage')}</option>
                          <option value="vi">Vietnamese (vi)</option>
                          <option value="en">English (en)</option>
                          <option value="pl">Polish (pl)</option>
                          <option value="uk">Ukrainian (uk)</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Tiền tệ của cửa hàng (mục #232).
                      Đứng NGAY TRÊN thuế suất vì hai ô này đi với nhau: cùng
                      quyết định con số tiền trên hoá đơn đọc ra thế nào. */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      {t('storeCurrency')}
                    </label>
                    {modalMode === 'view' ? (
                      <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                        {formData.currency
                          ? `${formData.currency} (${currencySymbol(formData.currency)})`
                          : '-'}
                      </div>
                    ) : (
                      <>
                        <select
                          value={formData.currency}
                          onChange={(e) => handleFieldChange('currency', e.target.value)}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-400 focus:outline-none"
                        >
                          {/* Lựa chọn rỗng phải CÓ: bỏ trống là "để BE áp mặc
                              định", một trạng thái hợp lệ — không phải lỗi
                              chưa chọn. */}
                          <option value="">{t('storeCurrencyUnset')}</option>
                          {SUPPORTED_CURRENCIES.map((code) => (
                            <option key={code} value={code}>
                              {code} ({currencySymbol(code)})
                            </option>
                          ))}
                          {/* Cửa hàng đang giữ một mã ngoài danh sách (BE nhận
                              chuỗi tự do) thì vẫn phải chọn lại được chính nó —
                              nếu không, mở form ra sửa địa chỉ là vô tình đổi
                              luôn tiền tệ sang mã đầu danh sách. */}
                          {formData.currency &&
                            !SUPPORTED_CURRENCIES.includes(formData.currency) && (
                              <option value={formData.currency}>{formData.currency}</option>
                            )}
                        </select>
                        <p className="mt-1 text-xs text-slate-400">{t('storeCurrencyHint')}</p>
                      </>
                    )}
                  </div>

                  {/* Thuế suất VAT mặc định (sheet 2 mục #77).
                      Giá niêm yết ĐÃ GỒM VAT — mức này để TÁCH phần thuế ra
                      khỏi giá khi in hoá đơn, không phải để cộng thêm. */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      {t('defaultTaxRate')}
                    </label>
                    {modalMode === 'view' ? (
                      <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                        {formData.defaultTaxRate ? `${formData.defaultTaxRate}%` : '-'}
                      </div>
                    ) : (
                      <>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.01"
                          value={formData.defaultTaxRate}
                          onChange={(e) => handleFieldChange('defaultTaxRate', e.target.value)}
                          placeholder={t('defaultTaxRatePlaceholder')}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                        />
                        <p className="mt-1 text-xs text-slate-400">{t('defaultTaxRateHint')}</p>
                      </>
                    )}
                  </div>

                  {/* Ảnh cửa hàng (sheet 2 mục #61) — BE bật image_urls trên
                      cả Create/Update từ 03/09/2026.
                      Bug #253: trước đây cả khối này bị ẩn ở mode 'view' vì
                      "màn xem không sửa được ảnh". Nhưng không sửa được không
                      có nghĩa là không được XEM — người dùng thêm ảnh lúc tạo,
                      mở chi tiết ra thì không thấy ảnh đâu và tưởng ảnh đã mất.
                      Mode 'view' giờ hiện ảnh dưới dạng chỉ-đọc; ô upload vẫn
                      chỉ có ở create/edit. */}
                  {modalMode === 'view' ? (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {t('storeImage')}
                      </label>
                      {formData.imageUrls.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {formData.imageUrls.map((url) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="block h-20 w-20 overflow-hidden rounded-lg border border-slate-200 transition-opacity hover:opacity-80"
                            >
                              <img
                                src={url}
                                alt={t('storeImage')}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">{t('storeNoImage')}</p>
                      )}
                    </div>
                  ) : (
                    <ImageUploadField
                      label={t('storeImage')}
                      value={formData.imageUrls}
                      onChange={(urls) =>
                        setFormData((prev) => ({ ...prev, imageUrls: urls }))
                      }
                      entityType="store"
                    />
                  )}

                  {/* Row 5: Operating Hours + Pricing Zone */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Operating Hours — collapsible dropdown */}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('operatingHours')}</label>
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setOhOpen((v) => !v)}
                          className="flex w-full items-center justify-between px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <span className="text-slate-500 truncate pr-2">
                            {DAYS.filter((d) => !formData.operating_hours[d].closed)
                              .map((d) => DAY_LABELS[d]).join(', ') || 'All closed'}
                          </span>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className={`shrink-0 text-slate-400 transition-transform ${ohOpen ? 'rotate-180' : ''}`}>
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>

                        {ohOpen && (
                          <div className="border-t border-slate-100 px-3 py-2.5 space-y-2">
                            {/* Chọn một khung giờ rồi Apply cho cả bảy ngày */}
                            {modalMode !== 'view' && (
                              <div className="flex items-center gap-1.5 pb-2 border-b border-slate-100">
                                <span className="text-xs font-medium text-slate-400 w-7 shrink-0">{t('all')}</span>
                                <TimeInput
                                  id="oh-apply-open"
                                  value={applyHours.open}
                                  onChange={(v) => setApplyHours((prev) => ({ ...prev, open: v }))}
                                />
                                <span className="text-slate-300 text-xs">–</span>
                                <TimeInput
                                  id="oh-apply-close"
                                  value={applyHours.close}
                                  onChange={(v) => setApplyHours((prev) => ({ ...prev, close: v }))}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const { open, close } = applyHours;
                                    const updated = { ...formData.operating_hours };
                                    // Áp cho TẤT CẢ các ngày, kể cả ngày đang Off:
                                    // cờ `closed` giữ nguyên, nên ngày nghỉ vẫn nghỉ,
                                    // nhưng bỏ tick Off là có sẵn giờ đúng.
                                    for (const d of DAYS) {
                                      updated[d] = { ...updated[d], open, close };
                                    }
                                    handleFieldChange('operating_hours', updated as any);
                                  }}
                                  className="ml-auto rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors whitespace-nowrap"
                                >{t('apply')}</button>
                              </div>
                            )}
                            {DAYS.map((day) => {
                              const dh = formData.operating_hours[day];
                              return (
                                <div key={day} className="flex items-center gap-1.5">
                                  <span className="w-7 shrink-0 text-xs font-medium text-slate-500">{DAY_LABELS[day]}</span>
                                  {modalMode === 'view' ? (
                                    <span className="text-xs text-slate-700">
                                      {dh.closed ? <span className="text-slate-400">{t('closed')}</span> : `${dh.open} – ${dh.close}`}
                                    </span>
                                  ) : (
                                    <>
                                      <TimeInput value={dh.open} disabled={dh.closed}
                                        onChange={(v) => handleFieldChange('operating_hours', { ...formData.operating_hours, [day]: { ...dh, open: v } } as any)} />
                                      <span className="text-slate-300 text-xs">–</span>
                                      <TimeInput value={dh.close} disabled={dh.closed}
                                        onChange={(v) => handleFieldChange('operating_hours', { ...formData.operating_hours, [day]: { ...dh, close: v } } as any)} />
                                      <label className="ml-auto flex items-center gap-1 cursor-pointer text-xs text-slate-400 select-none">
                                        <input type="checkbox" checked={dh.closed}
                                          onChange={(e) => handleFieldChange('operating_hours', { ...formData.operating_hours, [day]: { ...dh, closed: e.target.checked } } as any)}
                                          className="h-3 w-3 accent-red-400" />
                                        Off
                                      </label>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing Zone */}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('pricingZone')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.pricing_zone || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.pricing_zone = el; }}
                          value={formData.pricing_zone}
                          onChange={(e) => handleFieldChange('pricing_zone', e.target.value)}
                          placeholder={t('enterPricingZone')}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                        />
                      )}
                    </div>
                  </div>

                  {/* Row 6: Tax Profile Ref + Invoice Profile Ref */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('taxProfileRef')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.tax_profile_ref || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.tax_profile_ref = el; }}
                          value={formData.tax_profile_ref}
                          onChange={(e) => handleFieldChange('tax_profile_ref', e.target.value)}
                          placeholder={t('enterTaxProfileRef')}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                        />
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('invoiceProfileRef')}</label>
                      {modalMode === 'view' ? (
                        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.invoice_profile_ref || '-'}</div>
                      ) : (
                        <input
                          ref={(el) => { fieldRefs.current.invoice_profile_ref = el; }}
                          value={formData.invoice_profile_ref}
                          onChange={(e) => handleFieldChange('invoice_profile_ref', e.target.value)}
                          placeholder={t('enterInvoiceProfileRef')}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                        />
                      )}
                    </div>
                  </div>

                  {/* Row 7: Local Backend URL */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('localBackendUrl')}</label>
                    {modalMode === 'view' ? (
                      <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.local_backend_url || '-'}</div>
                    ) : (
                      <input
                        ref={(el) => { fieldRefs.current.local_backend_url = el; }}
                        value={formData.local_backend_url}
                        onChange={(e) => handleFieldChange('local_backend_url', e.target.value)}
                        onBlur={() => handleFieldBlur('local_backend_url')}
                        placeholder="https://..."
                        className={`h-10 w-full rounded-lg border px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none ${formErrors.local_backend_url && formTouched.local_backend_url ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                      />
                    )}
                    {formErrors.local_backend_url && formTouched.local_backend_url && <p className="mt-1 text-xs text-red-600">{formErrors.local_backend_url}</p>}
                  </div>

                  {/* Row 8: Partner Server URL */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('partnerServerUrl')}</label>
                    {modalMode === 'view' ? (
                      <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.partner_server_url || '-'}</div>
                    ) : (
                      <input
                        ref={(el) => { fieldRefs.current.partner_server_url = el; }}
                        value={formData.partner_server_url}
                        onChange={(e) => handleFieldChange('partner_server_url', e.target.value)}
                        onBlur={() => handleFieldBlur('partner_server_url')}
                        placeholder="https://..."
                        className={`h-10 w-full rounded-lg border px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none ${formErrors.partner_server_url && formTouched.partner_server_url ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                      />
                    )}
                    {formErrors.partner_server_url && formTouched.partner_server_url && <p className="mt-1 text-xs text-red-600">{formErrors.partner_server_url}</p>}
                  </div>

                  {/* Row 9: Metadata */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('metadata')}</label>
                    {modalMode === 'view' ? (
                      <div className="min-h-[60px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.metadata || '-'}</div>
                    ) : (
                      <textarea
                        ref={(el) => { fieldRefs.current.metadata = el as any; }}
                        value={formData.metadata}
                        onChange={(e) => handleFieldChange('metadata', e.target.value)}
                        placeholder="Enter metadata (JSON or string)"
                        rows={2}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none resize-none"
                      />
                    )}
                  </div>

                  {/**
                    * Giờ cấm bán rượu — hiển thị ở CẢ BA chế độ (create/edit/view),
                    * dùng CHUNG một khối JSX, không nhân bản.
                    *
                    * Ràng buộc cũ của backend ("POST không nhận nhóm này") đã hết
                    * từ 26/08/2026: cả create lẫn update đều gửi cùng một payload
                    * qua `buildCurfewPayload`.
                    */}
                  <div className="rounded-xl border border-slate-200 p-4">
                      <p className="mb-3 text-sm font-semibold text-slate-900">{t('alcoholCurfew')}</p>

                      {/**
                        * Bản BE đang chạy không trả `alcohol_curfew_config`, nên
                        * mấy ô dưới đây KHÔNG phản ánh trạng thái thật trên
                        * server. Nói thẳng ra, nếu không người test sẽ thấy
                        * checkbox tắt và tưởng cấu hình đã mất.
                        *
                        * Kèm lời hứa quan trọng hơn: bấm Lưu cũng KHÔNG đụng tới
                        * phần này (payload bỏ hẳn key — xem `toUpdatePayload`).
                        *
                        * Chú thích tự biến mất khi BE deploy field mới.
                        */}
                      {!formData.curfew_status.config_available && (
                        <p className="mb-3 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
                          {t('curfewConfigUnavailableHint')}
                        </p>
                      )}

                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          disabled={modalMode === 'view'}
                          checked={formData.consumption_on_premise}
                          onChange={(e) => handleCurfewChange('consumption_on_premise', e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200 disabled:opacity-60"
                        />
                        <span className="text-sm text-slate-700">{t('consumptionOnPremise')}</span>
                      </label>
                      {/* Giải thích chung cho ô tick "uống tại chỗ". Nhường chỗ
                          khi đã có chú thích cụ thể ở dưới (`curfewInactiveReason
                          === 'on_premise'`) để không hiện hai câu cùng nghĩa. */}
                      {formData.consumption_on_premise && curfewInactiveReason !== 'on_premise' && (
                        <p className="mt-1 text-xs text-slate-500">{t('consumptionOnPremiseHint')}</p>
                      )}

                      <label className="mt-3 flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          disabled={modalMode === 'view'}
                          checked={formData.curfew_enabled}
                          onChange={(e) => handleCurfewChange('curfew_enabled', e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200 disabled:opacity-60"
                        />
                        <span className="text-sm text-slate-700">{t('curfewEnabled')}</span>
                      </label>

                      {/**
                        * "Cấu hình còn nguyên, chỉ là hôm nay chưa áp."
                        *
                        * Lấy từ `alcohol_curfew_config.applies_today` /
                        * `suppressed_by_on_premise` — hai cờ BE thêm đúng để form
                        * nói được câu này. Thiếu nó, app user không thấy giờ cấm
                        * còn admin thì thấy cấu hình vẫn bật, và không ai giải
                        * thích được vênh ở đâu.
                        *
                        * Nhánh `out_of_range` VẪN PHẢI GIỮ dù form đã bỏ hai ô
                        * ngày hiệu lực: server có thể còn ngày đặt từ trước, và
                        * khi hôm nay rơi ngoài khoảng đó thì giờ cấm không chạy.
                        * Im lặng ở đây = người dùng thấy checkbox bật mà app
                        * không cấm gì, không hiểu tại sao.
                        *
                        * Câu chữ dùng key RIÊNG `curfewInactiveOutOfRangeReadOnly`
                        * chứ không phải `curfewInactiveOutOfRange`: bản cũ bảo
                        * người dùng đi sửa ngày hiệu lực, mà ô đó không còn nữa —
                        * hướng dẫn một việc không làm được còn tệ hơn không nói.
                        */}
                      {curfewInactiveReason && (
                        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          {curfewInactiveReason === 'on_premise'
                            ? t('curfewInactiveOnPremise')
                            : t('curfewInactiveOutOfRangeReadOnly')}
                        </p>
                      )}

                      {formData.curfew_enabled && (
                        <div className="mt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('curfewStartTime')}</label>
                              {modalMode === 'view' ? (
                                <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.curfew_start_time || '-'}</div>
                              ) : (
                                <input
                                  type="time"
                                  value={formData.curfew_start_time}
                                  onChange={(e) => handleCurfewChange('curfew_start_time', e.target.value)}
                                  className={`h-10 w-full rounded-lg border px-3 text-sm focus:border-blue-400 focus:outline-none ${formErrors.curfew_start_time && formTouched.curfew_start_time ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                                />
                              )}
                              {formErrors.curfew_start_time && formTouched.curfew_start_time && <p className="mt-1 text-xs text-red-600">{formErrors.curfew_start_time}</p>}
                            </div>
                            <div>
                              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('curfewEndTime')}</label>
                              {modalMode === 'view' ? (
                                <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{formData.curfew_end_time || '-'}</div>
                              ) : (
                                <input
                                  type="time"
                                  value={formData.curfew_end_time}
                                  onChange={(e) => handleCurfewChange('curfew_end_time', e.target.value)}
                                  className={`h-10 w-full rounded-lg border px-3 text-sm focus:border-blue-400 focus:outline-none ${formErrors.curfew_end_time && formTouched.curfew_end_time ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                                />
                              )}
                              {formErrors.curfew_end_time && formTouched.curfew_end_time && <p className="mt-1 text-xs text-red-600">{formErrors.curfew_end_time}</p>}
                            </div>
                          </div>

                          {/* Múi giờ đứng một mình nên để TRÀN CẢ HÀNG, không
                              nhét vào lưới 2 cột: ô còn lại của lưới sẽ trống
                              và khối nhìn lệch hẳn sang trái. */}
                          <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('curfewTimezone')}</label>
                            {modalMode === 'view' ? (
                              <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                                {formData.curfew_timezone ? formatTimezoneLabel(formData.curfew_timezone) : '-'}
                              </div>
                            ) : (
                              /**
                               * CHỌN, không cho gõ tay: một ký tự sai
                               * (`Europe/Warszawa`, `GMT+2`) là giờ cấm tính lệch
                               * mà BE chỉ vỡ lúc chạy, không ai thấy ở form.
                               */
                              <select
                                value={formData.curfew_timezone}
                                onChange={(e) => handleCurfewChange('curfew_timezone', e.target.value)}
                                className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none"
                              >
                                {/**
                                  * Rỗng LÀ hợp lệ ở ô NÀY: payload gửi
                                  * `timezone: null` và BE kế thừa múi giờ của
                                  * cửa hàng. (Ô múi giờ cấp cửa hàng thì
                                  * ngược lại — không có option rỗng.)
                                  *
                                  * Hiện luôn múi giờ ĐANG CHỌN ở ô cửa hàng để
                                  * người dùng biết "kế thừa" là kế thừa cái
                                  * gì. Câu "dùng múi giờ mặc định của cửa
                                  * hàng" chung chung chính là thứ khiến người
                                  * dùng tưởng đang trỏ về một giá trị an toàn
                                  * trong khi cửa hàng chưa khai gì cả.
                                  *
                                  * Chưa chọn múi giờ cửa hàng thì không có gì
                                  * để chèn vào câu → rơi về key cũ.
                                  */}
                                <option value="">
                                  {formData.timezone.trim()
                                    ? t('curfewTimezoneInheritsStore', { value: formatTimezoneLabel(formData.timezone.trim()) })
                                    : t('curfewTimezoneDefault')}
                                </option>
                                {/* Giá trị server đang lưu nhưng ngoài danh sách
                                    (store cũ nhập tay) — phải có option riêng,
                                    nếu không select tự nhảy về option đầu và
                                    lần Lưu kế tiếp đổi múi giờ âm thầm. */}
                                {currentCurfewTimezoneOption && (
                                  <option value={currentCurfewTimezoneOption.value}>
                                    {t('curfewTimezoneCurrentSaved', { value: currentCurfewTimezoneOption.label })}
                                  </option>
                                )}
                                {EUROPEAN_TIMEZONE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('storesStatus')}</label>
                    {modalMode === 'view' ? (
                      <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                        {formData.business_status === 'ACTIVE' ? t('active') : formData.business_status === 'INACTIVE' ? t('inactive') : formData.business_status === 'MAINTENANCE' ? t('maintenance') : '-'}
                      </div>
                    ) : (
                      <select
                        ref={(el) => { fieldRefs.current.business_status = el as any; }}
                        value={formData.business_status}
                        onChange={(e) => handleFieldChange('business_status', e.target.value)}
                        className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none"
                      >
                        <option value="ACTIVE">{t('storesActive')}</option>
                        <option value="INACTIVE">{t('storesInactive')}</option>
                        <option value="MAINTENANCE">{t('maintenance')}</option>
                      </select>
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-5">
              {/* Right: Cancel + Save */}
              <div className="flex items-center gap-3">
                <button
                  onClick={closeModal}
                  className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  {t('cancel')}
                </button>
                {modalMode !== 'view' && (
                  <button
                    onClick={handleSubmitForm}
                    disabled={isSubmitting}
                    className="rounded-lg bg-[#008CD2] px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                  >
                    {isSubmitting ? t('savingBtn') : t('saveBtn')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirm Popup */}
      {deleteConfirmId && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.45)' }}
          onClick={() => setDeleteConfirmId(null)}
        >
          <div
            className="w-full max-w-[440px] rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">
                {isActivating ? 'Activate Store' : t('deleteStore')}
              </h3>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100" />
            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-slate-500">
                {isActivating ? 'Are you sure you want to activate this store?' : 'Are you sure you want to deactivate this store?'}
              </p>
            </div>
            <div className="border-t border-slate-100" />
            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const nextStatus = isActivating ? 'ACTIVE' : 'INACTIVE';
                    await updateStore.mutateAsync({ id: deleteConfirmId, data: { business_status: nextStatus } });
                    setDeleteConfirmId(null);
                    closeModal();
                  } catch (err) {
                    setDeleteConfirmId(null);
                    showToast(
                      err instanceof Error
                        ? err.message
                        : isActivating ? t('failedToActivateStore') : t('failedToDeactivateStore'),
                      'error'
                    );
                  }
                }}
                disabled={updateStore.isPending}
                className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors ${isActivating ? 'bg-[#22C55E] hover:bg-green-600' : 'bg-[#F44336] hover:bg-red-600'} disabled:opacity-60`}
              >
                {updateStore.isPending ? (isActivating ? 'Activating...' : 'Deactivating...') : (isActivating ? 'Activate' : 'Deactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
